// src/components/ProtectedRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { useUser } from '../contexts/UserContext';
import { isTokenExpired, getAccessToken } from '../utils/auth';
import { refreshToken, logout } from '../services/authService';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  // From UserContext
  const { userProfile, userLoading } = useUser();

  // Local states to track token validity checking
  const [checkingToken, setCheckingToken] = useState(true);
  const [hasValidToken, setHasValidToken] = useState(false);

  useEffect(() => {
    // On mount, check if the existing token is valid or needs refresh
    const token = getAccessToken();

    if (!token || isTokenExpired(token)) {
      // Attempt to refresh
      refreshToken()
        .then((newToken) => {
          if (newToken && !isTokenExpired(newToken)) {
            setHasValidToken(true);
          } else {
            setHasValidToken(false);
          }
          setCheckingToken(false);
        })
        .catch(() => {
          setHasValidToken(false);
          setCheckingToken(false);
        });
    } else {
      // Token is valid
      setHasValidToken(true);
      setCheckingToken(false);
    }
  }, []);

  // 1. If still checking token or user data is loading, show a spinner
  if (checkingToken || userLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 2. If no valid token => must go to /login
  if (!hasValidToken) {
    return <Navigate to="/login" replace />;
  }

  // 3. If token is valid, but we never got a profile => also unauthorized
  if (!userProfile) {
    return <Navigate to="/login" replace />;
  }

  // 4. Check role-based authorization
  if (allowedRoles.length > 0 && !allowedRoles.includes(userProfile.role)) {
    // If the user doesn’t have the correct role => redirect away
    return <Navigate to="/dashboard" replace />;
  }

  // 5. Otherwise, show the protected content
  return children;
};

export default ProtectedRoute;
